import { useEffect, useMemo, useState } from 'react';
import api from '@/api';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Layers, Search } from 'lucide-react';

const SOURCE_STYLES = {
  demaxtore: 'bg-blue-50 text-blue-700',
  commoditybid: 'bg-violet-50 text-violet-700',
};

const StatusBadge = ({ status }) => {
  const s = String(status || '—').toLowerCase();
  const map = {
    pending: 'bg-amber-50 text-amber-700',
    open: 'bg-amber-50 text-amber-700',
    active: 'bg-green-50 text-green-700',
    awarded: 'bg-blue-50 text-blue-700',
    closed: 'bg-slate-50 text-slate-500',
    completed: 'bg-slate-50 text-slate-600',
  };
  return (
    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${map[s] || 'bg-slate-50 text-slate-600'}`}>
      {status || '—'}
    </span>
  );
};

function formatDate(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

export default function TradeActivity() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, demaxtore: 0, commoditybid: 0 });
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('all');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    api
      .get('/admin/trade-activity')
      .then((r) => {
        setItems(r.data?.items || []);
        setCounts(r.data?.counts || { total: 0, demaxtore: 0, commoditybid: 0 });
        setNote(r.data?.commoditybidNote || '');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const statusOptions = useMemo(() => {
    const set = new Set();
    items.forEach((i) => {
      const s = String(i.status || '').trim();
      if (s) set.add(s);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const typeOptions = useMemo(() => {
    const map = new Map();
    items.forEach((i) => {
      if (i.type) map.set(i.type, i.typeLabel || i.type);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (source !== 'all' && item.source !== source) return false;
      if (type !== 'all' && item.type !== type) return false;
      if (status !== 'all' && String(item.status).toLowerCase() !== status.toLowerCase()) return false;
      if (!q) return true;
      const hay = [
        item.title,
        item.customerName,
        item.customerEmail,
        item.typeLabel,
        item.status,
        item.supplierName,
        item.category,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, source, type, status, search]);

  return (
    <div className="space-y-5" data-testid="admin-trade-activity-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Trade activity</h1>
        <p className="text-slate-500 text-sm mt-0.5">
          DeMaxtore RFQs and CommodityBid requests, bids, and offers in one place
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { label: 'Total records', value: counts.total },
          { label: 'DeMaxtore', value: counts.demaxtore },
          { label: 'CommodityBid (live)', value: counts.commoditybid },
        ].map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-slate-200 px-4 py-3">
            <p className="text-xs text-slate-500 font-medium">{c.label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-0.5">{c.value}</p>
          </div>
        ))}
      </div>

      {note ? (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">{note}</p>
      ) : null}

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col lg:flex-row gap-3 lg:items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-slate-500 mb-1 block">Search</label>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              className="pl-9"
              placeholder="Title, customer, email, supplier…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="trade-activity-search"
            />
          </div>
        </div>
        <div className="w-full sm:w-40">
          <label className="text-xs font-medium text-slate-500 mb-1 block">Source</label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="trade-activity-source">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="demaxtore">DeMaxtore</SelectItem>
              <SelectItem value="commoditybid">CommodityBid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-44">
          <label className="text-xs font-medium text-slate-500 mb-1 block">Type</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="trade-activity-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {typeOptions.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-40">
          <label className="text-xs font-medium text-slate-500 mb-1 block">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="trade-activity-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statusOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-14 bg-white rounded-xl border animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Layers className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No records match your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]" data-testid="admin-trade-activity-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">
                      Customer
                    </th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                      Date
                    </th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtered.map((item) => (
                    <tr key={`${item.source}-${item.type}-${item.id}-${item.customerEmail}`} className="hover:bg-slate-50">
                      <td className="px-5 py-3.5">
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            SOURCE_STYLES[item.source] || 'bg-slate-50 text-slate-600'
                          }`}
                        >
                          {item.source === 'commoditybid' ? 'CommodityBid' : 'DeMaxtore'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-600">{item.typeLabel || item.type}</td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-medium text-slate-900">{item.title}</p>
                        {item.supplierName ? (
                          <p className="text-xs text-slate-400 mt-0.5">Supplier: {item.supplierName}</p>
                        ) : null}
                        {item.amount != null ? (
                          <p className="text-xs text-slate-400 mt-0.5">Amount: {item.amount}</p>
                        ) : null}
                      </td>
                      <td className="px-5 py-3.5 hidden md:table-cell">
                        <p className="text-sm text-slate-700">{item.customerName}</p>
                        <p className="text-xs text-slate-400">{item.customerEmail}</p>
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell text-sm text-slate-500">
                        {formatDate(item.createdAt)}
                      </td>
                      <td className="px-5 py-3.5">
                        <StatusBadge status={item.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-slate-400 px-5 py-3 border-t border-slate-50">
            Showing {filtered.length} of {items.length} records
          </p>
        </div>
      )}
    </div>
  );
}
